import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Users, Headphones, BookOpen, Star, Heart, Shield, Award, Cigarette, Cloud } from "lucide-react";
import heroImage from "@/assets/hero-therapies.jpg";
import portraitNathalie from "@/assets/portrait-nathalie.webp";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/site/CTABanner";
import TestimonialCarousel from "@/components/site/TestimonialCarousel";
import SEOHead from "@/components/blog/SEOHead";
import ArticleCard from "@/components/blog/ArticleCard";
import { articles } from "@/data/blogContent";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BASE = "https://arreter-de-fumer-auxerre.fr";

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Nathalie Barach",
  jobTitle: "Hypnothérapeute & Coach",
  description: "Hypnothérapeute et coach à Chemilly-sur-Yonne, près d’Auxerre : arrêt du tabac, confiance en soi, phobies et deuil.",
  url: BASE,
  image: `${BASE}/og-image.png`,
  knowsAbout: ["Hypnose Ericksonienne", "DTMA", "Arrêt du tabac", "Coaching", "Confiance en soi", "Phobies", "Deuil"],
  worksFor: { "@type": "Organization", name: "Sérénité Vive" },
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const HomePage = () => (
  <>
    <SEOHead
      title="Nathalie Barach | Hypnose, DTMA & coaching près d’Auxerre"
      description="Hypnose, DTMA et coaching près d’Auxerre : tabac, confiance en soi, phobies et deuil. Nathalie Barach vous accueille au cabinet à Chemilly-sur-Yonne, dans l’Yonne."
      canonical={BASE}
      jsonLd={[personSchema]}
    />
    {/* Hero */}
    <section className="relative text-primary-foreground overflow-hidden min-h-[65svh] flex items-center -mt-px">
      <img src={heroImage} alt="Hypnose, DTMA et coaching près d’Auxerre" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" decoding="async" />
      <div className="absolute inset-0 gradient-hero opacity-80" />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-20 text-center w-full">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-body text-primary-foreground/70 text-sm uppercase tracking-widest mb-4"
        >
          Hypnose · DTMA · Coaching près d’Auxerre
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight"
        >
          Nathalie Barach
          <br className="hidden sm:block" />
          <span className="text-accent"> Hypnose, DTMA & coaching</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="font-body text-accent font-bold text-lg md:text-xl mb-4"
        >
          Arrêt du tabac · Confiance en soi · Phobies · Deuil
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="font-body text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-8"
        >
          Je vous accompagne pour vous libérer du tabac, retrouver confiance, apaiser vos peurs ou traverser un deuil. Au cabinet à Chemilly-sur-Yonne, j’avance avec vous, à votre rythme.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent text-accent-foreground font-body font-semibold text-base hover:brightness-105 transition-all"
          >
            Prendre rendez-vous <ArrowRight size={18} />
          </Link>
          <Link
            to="/therapies"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-primary-foreground/30 text-primary-foreground font-body text-base hover:bg-primary-foreground/10 transition-all"
          >
            Tous mes accompagnements
          </Link>
        </motion.div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 bg-background" id="accompagnements">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-10">
          <p className="font-body text-accent font-semibold mb-2">Mes accompagnements</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Un espace pour avancer, quel que soit votre besoin</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Cigarette, title: "Arrêt du tabac", text: "Faire évoluer votre relation à la cigarette, comprendre vos déclencheurs et construire de nouvelles habitudes.", to: "/arreter-le-tabac" },
            { icon: Heart, title: "Confiance en soi", text: "Apaiser le doute, retrouver votre estime personnelle et oser prendre votre place au quotidien.", to: "/therapies#confiance-en-soi" },
            { icon: Shield, title: "Gestion des phobies", text: "Travailler sur les réactions de peur et retrouver progressivement un sentiment de sécurité.", to: "/therapies#phobies" },
            { icon: Cloud, title: "Accompagnement au deuil", text: "Accueillir vos émotions et retrouver des repères, sans effacer le lien avec la personne disparue.", to: "/therapies#deuil" },
          ].map(({ icon: Icon, title, text, to }) => (
            <motion.div key={title} {...fadeUp} className="border border-border bg-card rounded-lg p-6 flex flex-col">
              <Icon size={28} className="text-secondary mb-5" />
              <h3 className="font-heading text-xl font-bold text-foreground mb-3">{title}</h3>
              <p className="font-body text-muted-foreground leading-relaxed mb-6 flex-1">{text}</p>
              <Button asChild variant="link" className="justify-start px-0 h-auto whitespace-normal"><Link to={to}>Découvrir cet accompagnement <ArrowRight size={16} /></Link></Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Intro / Qui suis-je */}
    <section className="py-20 px-4 sm:px-6 bg-background">
      <div className="max-w-5xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-14">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-widest mb-2">Votre hypnothérapeute</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Nathalie Barach</h2>
          <p className="font-body text-foreground/60 mt-2">Hypnothérapeute & Coach à Chemilly-sur-Yonne, près d’Auxerre</p>
        </motion.div>

        <motion.div {...fadeUp} className="flex flex-col md:flex-row items-center gap-10">
          <img
            src={portraitNathalie}
            alt="Portrait de Nathalie Barach, hypnothérapeute"
            loading="lazy"
            decoding="async"
            width="224"
            height="224"
            className="w-48 h-48 md:w-56 md:h-56 rounded-2xl object-cover object-top shadow-lg shrink-0"
          />
          <div className="text-center md:text-left">
            <p className="font-body text-foreground/70 text-lg leading-relaxed mb-6">
              Formée en hypnose Ericksonienne, DTMA et coaching, je vous accompagne dans les moments où vous avez besoin de changement, de soutien ou d’apaisement. Tabac, confiance, phobies ou deuil : votre histoire et vos besoins guident chaque séance.
            </p>
            <p className="font-body text-foreground/70 text-lg leading-relaxed mb-8">
              Je vous accueille au 15 rue Charles de Gaulle, 89250 Chemilly-sur-Yonne, dans un cadre d’écoute et de confidentialité. Nous construisons ensemble un accompagnement personnalisé, avec des outils concrets pour votre quotidien.
            </p>
            <Link
              to="/a-propos"
              className="inline-flex items-center gap-2 font-body text-primary font-semibold hover:underline"
            >
              En savoir plus sur mon parcours <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Pourquoi cet accompagnement */}
    <section className="py-20 px-4 sm:px-6 bg-muted/50">
      <div className="max-w-6xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-14">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-widest mb-2">Mon approche</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            Trois approches complémentaires, une écoute personnalisée
          </h2>
          <p className="font-body text-foreground/60 mt-4 max-w-2xl mx-auto">
            Nous choisissons ensemble les outils adaptés à votre objectif. Aucun parcours imposé : l’accompagnement évolue avec vos besoins et vos progrès.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: BookOpen, title: "Hypnose Ericksonienne", desc: "Mobiliser vos ressources intérieures, travailler sur les automatismes et apaiser les émotions." },
            { icon: Headphones, title: "DTMA", desc: "Travailler sur les mémoires émotionnelles et les réactions qui vous freinent, à votre rythme." },
            { icon: Users, title: "Coaching", desc: "Clarifier vos objectifs, trouver des repères et passer à l’action avec des outils concrets." },
            { icon: CheckCircle, title: "Suivi au cabinet", desc: "Des séances individuelles et un accompagnement ajusté à votre situation, près d’Auxerre." },
          ].map((item) => (
            <motion.div
              key={item.title}
              {...fadeUp}
              className="bg-card rounded-xl p-6 border border-border hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                <item.icon size={24} className="text-secondary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{item.title}</h3>
              <p className="font-body text-foreground/60 text-sm leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA Banner — Arrêter le tabac */}
    <section className="relative bg-primary overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-xl bg-primary-foreground/15 flex items-center justify-center shrink-0">
            <Heart size={28} className="text-primary-foreground" />
          </div>
          <div>
            <p className="font-heading text-lg sm:text-xl font-bold text-primary-foreground">
              Votre prochain rendez-vous au cabinet
            </p>
            <p className="font-body text-primary-foreground/70 text-base">
              15 rue Charles de Gaulle · 89250 Chemilly-sur-Yonne
            </p>
          </div>
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-foreground text-primary font-body font-semibold text-sm hover:brightness-95 transition-all shrink-0"
        >
          Prendre rendez-vous <ArrowRight size={16} />
        </Link>
      </div>
    </section>

    {/* Pourquoi choisir Nathalie */}
    <section className="py-20 px-4 sm:px-6 bg-background">
      <div className="max-w-6xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-14">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-widest mb-2">Pourquoi me choisir</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            Un accompagnement qui fait la différence
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {[
            { icon: Heart, title: "Écoute et bienveillance", desc: "Un espace pour parler de ce que vous vivez, sans jugement ni pression, avec respect pour votre histoire." },
            { icon: Shield, title: "Votre rythme respecté", desc: "Nous avançons progressivement. Vous restez acteur ou actrice de votre accompagnement, à chaque étape." },
            { icon: Star, title: "Des objectifs concrets", desc: "Arrêt du tabac, confiance, phobies ou deuil : nous définissons les changements qui comptent pour vous." },
            { icon: Award, title: "Une approche personnalisée", desc: "Hypnose, DTMA et coaching se complètent selon votre situation. Les séances ont lieu au cabinet à Chemilly-sur-Yonne." },
          ].map((item) => (
            <motion.div key={item.title} {...fadeUp} className="flex gap-4 p-6 bg-card rounded-xl border border-border">
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                <item.icon size={24} className="text-accent" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground mb-2">{item.title}</h3>
                <p className="font-body text-foreground/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust stats bar */}
        <motion.div {...fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "4", label: "Accompagnements" },
            { value: "100+", label: "Clients accompagnés" },
            { value: "3", label: "Approches complémentaires" },
            { value: "5/5", label: "Satisfaction client (26 avis)" },
          ].map((stat) => (
            <div key={stat.label}>
              <p className="font-heading text-3xl md:text-4xl font-bold text-primary">{stat.value}</p>
              <p className="font-body text-foreground/50 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* Citation */}
    <section className="py-12 px-4 sm:px-6 bg-muted/50">
      <motion.p {...fadeUp} className="font-heading text-xl md:text-2xl italic text-foreground/70 text-center max-w-3xl mx-auto">
        «&nbsp;La vie est belle, prenez-en soin.&nbsp;»
      </motion.p>
    </section>

    <TestimonialCarousel />

    {/* Mutuelle info */}
    <section className="py-16 px-4 sm:px-6 bg-muted/50">
      <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
          Remboursement mutuelle possible
        </h2>
        <p className="font-body text-foreground/60 mb-6">
          Selon votre contrat, vos séances d'hypnose peuvent être partiellement ou totalement remboursées par votre mutuelle. N'hésitez pas à vous renseigner directement auprès d'elle.
        </p>
        <Link
          to="/blog/arret-tabac-hypnose-mutuelle-remboursement"
          className="inline-flex items-center gap-2 font-body font-semibold text-primary hover:text-secondary transition-colors underline underline-offset-2"
        >
          En savoir plus sur le remboursement <ArrowRight size={16} />
        </Link>
      </motion.div>
    </section>

    {/* FAQ */}
    <section className="py-20 px-4 sm:px-6 bg-muted/50">
      <div className="max-w-3xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Questions fréquentes</h2>
        </motion.div>

        <motion.div {...fadeUp}>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: "Pour quels besoins puis-je prendre rendez-vous ?", a: "Je vous accompagne pour l’arrêt du tabac, la confiance en soi, la gestion des phobies et le deuil, en associant hypnose, DTMA et coaching selon votre situation." },
              { q: "Où se déroulent les séances ?", a: "Les rendez-vous ont lieu au cabinet au 15 rue Charles de Gaulle, 89250 Chemilly-sur-Yonne, près d’Auxerre dans l’Yonne. Attention : le cabinet a changé d’adresse." },
              { q: "Comment réserver ?", a: "Choisissez votre accompagnement et un créneau disponible sur la page Contact. Vous recevez un email récapitulatif avec la nouvelle adresse du cabinet." },
              { q: "Comment se déroule le premier rendez-vous ?", a: "Nous prenons le temps d’échanger sur votre situation et vos attentes, puis nous définissons le travail adapté. Il n’y a pas de vidéo ni de questionnaire préalable à regarder." },
              { q: "Les séances sont-elles remboursées ?", a: "Certaines mutuelles proposent une prise en charge de l’hypnose. Vérifiez les garanties de votre contrat. Je peux vous fournir une facture." },
              { q: "Cet accompagnement remplace-t-il un suivi médical ?", a: "Non. L’hypnose, la DTMA et le coaching ne remplacent pas un diagnostic, un traitement médical ou un suivi psychologique. Ils peuvent s’inscrire en complément d’un suivi adapté." },
            ].map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger className="font-body text-foreground font-semibold text-left text-base py-4 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="font-body text-foreground/60 text-sm leading-relaxed pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div {...fadeUp} className="text-center mt-8">
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 font-body font-semibold text-primary hover:text-secondary transition-colors"
          >
            Voir toutes les questions <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>

    {/* Derniers articles */}
    <section className="py-20 px-4 sm:px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp} className="text-center mb-14">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-widest mb-2">Actualités</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">Nos derniers articles</h2>
          <p className="font-body text-foreground/60 mt-4 max-w-2xl mx-auto">
            Hypnose, coaching, émotions et arrêt du tabac : des repères pour votre bien-être
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {articles.slice(0, 4).map((article, i) => (
            <ArticleCard key={article.slug} article={article} index={i} />
          ))}
        </div>

        <motion.div {...fadeUp} className="flex flex-wrap justify-center gap-4 mt-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-body font-semibold text-primary hover:text-secondary transition-colors"
          >
            Voir tous les articles <ArrowRight size={16} />
          </Link>
          <Link
            to="/arreter-le-tabac/guide-complet"
            className="inline-flex items-center gap-2 font-body font-semibold text-accent hover:text-secondary transition-colors"
          >
            Guide arrêt du tabac <ArrowRight size={16} />
          </Link>
          <Link
            to="/hypnose/guide-complet"
            className="inline-flex items-center gap-2 font-body font-semibold text-accent hover:text-secondary transition-colors"
          >
            Guide hypnose <ArrowRight size={16} />
          </Link>
          <Link
            to="/coaching/guide-complet"
            className="inline-flex items-center gap-2 font-body font-semibold text-accent hover:text-secondary transition-colors"
          >
            Guide coaching <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>

    <CTABanner />
  </>
);

export default HomePage;
